// Org management operations

import { TAoiUploadFileFormat } from "../../Aois/AoiUpload";
import { CallServer, CallServerResults } from "../../CallServer";
import Debug from "../../Debug";
import { GetGeojsonAcres, GetGeojsonPointCount, GetPolygonCount } from "../../GisOps";
import { ToastNotification } from "../../ToastNotifications";
import { IOrgManagement_Boundary, IOrgManagement_Data, IOrgManagement_Org } from "./OrgManagementInterfaces";
import { GeoJSON } from 'geojson';


//-------------------------------------------------------------------------------
// Load org and boundary data.
//-------------------------------------------------------------------------------
export async function LoadAdminOrgAndBoundaryData(setIsRunning: any = undefined): Promise<IOrgManagement_Data | undefined>
{
  // Call the server to get the data

  const server = new CallServer();

  if(setIsRunning) setIsRunning(true); // Signal the UI that we are loading the user info

  const result: CallServerResults = await server.Call('get', '/orgs');

  if(setIsRunning) setIsRunning(false); // Signal the UI that we are done loading the user info

  if(result.success)
  {
    const data: IOrgManagement_Data | undefined = result.data;
    if(!data)
    {
      ToastNotification('error', "Unable to load admin org and boundary data")
      return undefined;
    }

    // Calculate some extra stats for each boundary

    for(let i=0; i < data.boundaries.length; i++)
    {
      data.boundaries[i].polygons = GetPolygonCount(data.boundaries[i].geom);
      data.boundaries[i].points = GetGeojsonPointCount(data.boundaries[i].geom);
      data.boundaries[i].acres = GetGeojsonAcres(data.boundaries[i].geom);
    }

    // Sort the lists

    const sortedData: IOrgManagement_Data =
    {
      ...data,
      orgs: data.orgs.sort((a: IOrgManagement_Org, b: IOrgManagement_Org) => a.name.localeCompare(b.name)),
      boundaries: data.boundaries.sort((a: IOrgManagement_Boundary, b: IOrgManagement_Boundary) => a.name.localeCompare(b.name)),
    }

    // Success
    return sortedData;
  }
  else
  {
    // Failure
    ToastNotification('error', "Unable to load admin org and boundary data")
    return undefined;
  }
}

//-------------------------------------------------------------------------------
// Create/update an org.
//-------------------------------------------------------------------------------
export async function CreateOrUpdateOrg(org: IOrgManagement_Org , setIsRunning: any = undefined): Promise<{ret: boolean, new_id?: number}>
{
  if(!org) return {ret: false};

  const createMode: boolean = org.id === undefined || org.id === null;

  // Remove a couple of things that don't need to be sent back (like 'expired' and 'stats')
  const fixedOrg = 
  {
    id: org.id,
    name: org.name,
    state: org.state,
    boundary_id: org.boundary_id ?? null,
    expiration_date: org.expiration_date ?? null,
    settings: org.settings
  }

  // Call the server

  const server = new CallServer();
  server.Add('org', fixedOrg);

  if(setIsRunning) setIsRunning(true); // Signal the UI that we are loading the user info

  const result: CallServerResults = await server.Call('post', '/orgs');

  if(setIsRunning) setIsRunning(false); // Signal the UI that we are done loading the user info

  if(result.success)
  {
    if(createMode)
    {
      const newOrgID: number | undefined = result.data.org_id;
      if(!newOrgID)
        Debug.warn('Received invalid new org ID');

      ToastNotification('success', `New organization created (id ${newOrgID})` );

      // Success - return the new org ID
      return {ret: true, new_id: newOrgID};
    }
    else // Edit mode
    {
      ToastNotification('success', 'Organization updated');

      // Success
      return {ret: true};
    }
  }
  else
  {
    // Failure
    ToastNotification('error', `Unable to ${createMode ? 'create' : 'update'} organization`)
    return {ret: false};
  }
}

//-------------------------------------------------------------------------------
// Delete an org.
//-------------------------------------------------------------------------------
export async function DeleteOrg(org: IOrgManagement_Org, setIsRunning: any = undefined): Promise<boolean>
{
  if(!org) return false;

  // Call the server

  const server = new CallServer();
  server.Add('org_id', org.id);

  if(setIsRunning) setIsRunning(true); // Signal the UI that we are loading the user info

  const result: CallServerResults = await server.Call('delete', '/org');

  if(setIsRunning) setIsRunning(false); // Signal the UI that we are done loading the user info

  if(result.success)
  {
    ToastNotification('success', 'The organization has been deleted');

    // Success
    return true;
  }
  else
  {
    // Failure
    ToastNotification('error', "Unable to delete org")
    return false;
  }
}

//-------------------------------------------------------------------------------
// Delete a boundary.
//-------------------------------------------------------------------------------
export async function DeleteBoundary(boundary: IOrgManagement_Boundary, setIsRunning: any = undefined): Promise<boolean>
{
  if(!boundary) return false;

  // Call the server

  const server = new CallServer();
  server.Add('boundary_id', boundary.id);

  if(setIsRunning) setIsRunning(true); // Signal the UI that we are loading the user info

  const result: CallServerResults = await server.Call('delete', '/boundary');

  if(setIsRunning) setIsRunning(false); // Signal the UI that we are done loading the user info

  if(result.success)
  {
    ToastNotification('success', 'The boundary has been deleted');

    // Success
    return true;
  }
  else
  {
    // Failure
    ToastNotification('error', "Unable to delete boundary")
    return false;
  }
}

//-------------------------------------------------------------------------------
// Returns the number of orgs that use the specified boundary.
//-------------------------------------------------------------------------------
export function GetBoundaryUseCount(orgData: IOrgManagement_Data | undefined, boundary_id: number | null): number | undefined
{
  if(!boundary_id || !orgData) return undefined;

  let count: number = 0;
  for(let i=0; i < orgData.orgs.length; i++)
    if(orgData.orgs[i].boundary_id === boundary_id)
      count++;

  return count;
}

//-------------------------------------------------------------------------------
// Convert a local file into a MultiPolygon GeoJSON.
//-------------------------------------------------------------------------------
export async function ConvertFileToGeoJSON(file: any, format: TAoiUploadFileFormat, setIsRunning: any = undefined): Promise<GeoJSON | undefined>
{
  // Call the server to get the data

  const server = new CallServer();
  server.AddFile(file);
  server.Add('file_type', format);

  if(setIsRunning) setIsRunning(true); // Signal the UI that we are loading the user info

  const result: CallServerResults = await server.UploadFile('/file_to_geojson');

  if(setIsRunning) setIsRunning(false); // Signal the UI that we are done loading the user info

  if(result.success)
  {
    const geojson: GeoJSON | undefined = result.data;
    if(!geojson)
    {
      ToastNotification('error', "Unable to process file")
      return undefined;
    }

    // Success
    return geojson;
  }
  else
  {
    // Failure
    ToastNotification('error', "Unable to process file")
    return undefined;
  }
}

//-------------------------------------------------------------------------------
// Create or update a boundary.
//-------------------------------------------------------------------------------
export async function CreateOrUpdateBoundary(boundary: IOrgManagement_Boundary, setIsRunning: any = undefined): Promise<{ret: boolean, new_id?: number}>
{
  if(!boundary) return { ret: false };

  const createMode: boolean = boundary.id === undefined || boundary.id === null;

  // Call the server

  const server = new CallServer();
  if(boundary.id)
    server.Add('boundary_id', boundary.id);  // this determines create vs update
  server.Add('name', boundary.name);
  server.Add('boundary', JSON.stringify(boundary.geom));
  //server.Add('settings', boundary.settings);  // settings is not implemented in the UI YET
  //server.Add('notes', boundary.notes);  // Don't have notes implemented in the UI

  if(setIsRunning) setIsRunning(true); // Signal the UI that we are loading the user info

  const result: CallServerResults = await server.Call('post', '/boundary');

  if(setIsRunning) setIsRunning(false); // Signal the UI that we are done loading the user info

  if(result.success)
  {
    if(createMode)  // We created a new org
    {
      const new_boundary_id: number | undefined = result.data.boundary_id;
      if(new_boundary_id === undefined)
        Debug.warn('Received invalid new boundary ID');

      ToastNotification('success', `The boundary has been created (id ${new_boundary_id})`);

      // Success - return the new ID
      return { ret: true, new_id: new_boundary_id };
    }
    else // Edit mode
    {
      ToastNotification('success', 'The boundary has been updated');

      // Success
      return { ret: true };
    }
  }
  else
  {
    // Failure
    ToastNotification('error', `Unable to ${createMode ? 'create' : 'update'} boundary`)
    return { ret: false };
  }
}